/**
 * template/sections/education/EducationExamAuthBridge.jsx
 *
 * READER-only auth injection for the Exam Portal sections.
 *
 * The Exam section components are near-identical twins of the Console builder versions
 * (admin-console-ui/src/modules/template-builder/sections/...), which is what keeps the
 * builder preview and the published page from drifting. The Console has no auth store, so
 * the twins cannot import `useTemplateAuthState` themselves — instead they accept an `auth`
 * prop and this reader-side bridge supplies it.
 *
 * Signed in  → the nav renders the account avatar and drops sign-in/sign-up nav entries.
 * Signed out → the nav renders the Login / Register pair (also what the builder shows).
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { setSelectedProduct } from '../../../api/reducers/selectedProductReducer';
import {
  getAllValidProducts,
  getCurriculumsByPrimaryId,
  getCourseProductById,
  getScheduledProductsByProductCode,
  getUpcomingProducts,
  getValidProducts,
} from '../../../api/action/products';
import { getAcademyExamByUserId } from '../../../api/action/exam';
import { getReservationSlots } from '../../../api/action/booking';
import EducationExamHomeSection, { getExamAttemptStatistics, resolveExamAuth } from './EducationExamHomeSection.jsx';
import EducationExamPagesSection, { getExamCourseDetailPath } from './EducationExamPagesSection.jsx';
import { ExistingComponentSection } from '../shared/ExistingComponentSection.jsx';
import { getTemplateAccountRoute, useTemplateAuthState } from '../../TemplateAuthState.jsx';
import { useCoursePurchase } from '../../../views/products/useCoursePurchase';
import Login from '../../../views/loginOrRegister/Login.jsx';
import Signup from '../../../views/loginOrRegister/Signup.jsx';
import { useAuthSession } from '../../../auth/useAuthSession.js';

const EDUCATION = 'EDUCATION';

/** Auth state plus the domain's account route, shaped for the section nav. */
const useExamNavAuth = () => {
  const auth = useTemplateAuthState();
  const session = useAuthSession();
  const sessionIsLoggedIn = session.isAuthenticated || session.isLoggedIn;
  return React.useMemo(
    () => ({
      ...resolveExamAuth(auth, { isLoggedIn: sessionIsLoggedIn }),
      accountRoute: getTemplateAccountRoute(EDUCATION)
    }),
    [auth, sessionIsLoggedIn]
  );
};

export const EducationExamHomeSectionWithAuth = (props) => (
  <EducationExamHomeSection
    {...props}
    data={useExamSectionData(props.data)}
    auth={useExamNavAuth()}
    onSelectCourse={useSelectCourse()}
    onNavigate={useExamNavigate()}
  />
);

const asList = (response) => {
  if (Array.isArray(response?.payload?.data)) return response.payload.data;
  if (Array.isArray(response?.payload)) return response.payload;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return Array.isArray(response) ? response : [];
};

/**
 * Course list for the Exam pages, fetched exactly as the Default course page does.
 *
 * `useEducationTemplateData` (which feeds template sections generally) calls only
 * `getAllValidProducts`. The Default course page fetches THREE endpoints and merges them —
 * `getAllValidProducts` + `getValidProducts` + `getUpcomingProducts`, deduped by `primaryId`
 * (see fetchProducts in views/products/index.jsx). A company whose courses come back from
 * `getValidProducts` therefore showed an empty Exam catalog while the Default page listed them.
 * Replicating the same three calls and the same merge order keeps both pages in agreement.
 */
const useExamCatalogProducts = () => {
  const dispatch = useDispatch();
  const companyId = useSelector((state) => (
    state.companyReducer?.company?.primaryId ?? state.company?.companyDetails?.primaryId
  ));
  const [state, setState] = React.useState({ products: [], loading: Boolean(companyId), error: null, loaded: false });
  const [requestVersion, setRequestVersion] = React.useState(0);

  React.useEffect(() => {
    if (!companyId) {
      setState({ products: [], loading: false, error: null, loaded: true });
      return undefined;
    }
    let mounted = true;
    setState((current) => ({ ...current, loading: true, error: null }));

    const load = async () => {
      // Mirrors the Default page, which forwards ?category= through as the search term.
      const searchTerm = new URLSearchParams(window.location.search).get('category') || '';
      try {
        const [allResult, validResult, upcomingResult] = await Promise.all([
          dispatch(getAllValidProducts(companyId, searchTerm)),
          dispatch(getValidProducts(companyId, searchTerm)),
          dispatch(getUpcomingProducts(companyId)),
        ]);

        const all = asList(allResult);
        const valid = asList(validResult);
        const upcoming = asList(upcomingResult);
        const merged = [...all, ...valid.filter((item) => !all.some((existing) => existing.primaryId === item.primaryId))];
        const full = [...merged, ...upcoming.filter((item) => !merged.some((existing) => existing.primaryId === item.primaryId))];

        const seen = new Set();
        const deduped = [];
        full.forEach((item) => {
          if (item && !seen.has(item.primaryId)) {
            seen.add(item.primaryId);
            deduped.push(item);
          }
        });

        if (mounted) setState({ products: deduped, loading: false, error: null, loaded: true });
      } catch (error) {
        if (mounted) setState({ products: [], loading: false, error, loaded: true });
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [companyId, dispatch, requestVersion]);

  return React.useMemo(() => ({
    ...state,
    retry: () => setRequestVersion((version) => version + 1)
  }), [state]);
};

const useExamAttemptStatistics = () => {
  const authUser = useSelector((state) => state.auth?.data || state.auth?.user || state.user?.data);
  const userId = authUser?.primaryId || localStorage.getItem('userId');
  const [state, setState] = React.useState({ loaded: false, statistics: null });

  React.useEffect(() => {
    let mounted = true;
    if (!userId) {
      setState({ loaded: true, statistics: getExamAttemptStatistics([]) });
      return () => { mounted = false; };
    }

    setState((current) => ({ ...current, loaded: false }));
    getAcademyExamByUserId(userId)
      .then((response) => {
        if (mounted) setState({ loaded: true, statistics: getExamAttemptStatistics(asList(response)) });
      })
      .catch(() => {
        if (mounted) setState({ loaded: true, statistics: getExamAttemptStatistics([]) });
      });

    return () => { mounted = false; };
  }, [userId]);

  return state;
};

/** Prefers the fully merged course list, falling back to whatever the template data supplied. */
const useExamSectionData = (data) => {
  const catalog = useExamCatalogProducts();
  const attemptStatistics = useExamAttemptStatistics();
  const company = useSelector((state) => (
    state.companyReducer?.company || state.company?.companyDetails
  ));
  return React.useMemo(
    () => ({
      ...(data || {}),
      products: catalog.loaded ? catalog.products : (data?.products || []),
      examCatalogLoading: catalog.loading,
      examCatalogError: catalog.error,
      retryExamCatalog: catalog.retry,
      examCatalogLoaded: catalog.loaded,
      attemptStatistics: attemptStatistics.loaded
        ? attemptStatistics.statistics
        : data?.attemptStatistics,
      company: company || data?.company
    }),
    [attemptStatistics, catalog, company, data]
  );
};

/** Mirrors CourseInfo's isScheduleRequired: instructor-led or in-person courses need a slot. */
const scheduleRequired = (course) => {
  if (!course) return false;
  if (isInterviewProduct(course)) return false;
  if (course.deliveryType === 'BRICK_MORTAR') return true;
  return course.deliveryType === 'VIRTUAL' && course.deliverySubType === 'INSTRUCTOR_LED';
};

const isValidExamSchedule = (schedule) => {
  const status = String(schedule?.status || '').toUpperCase();
  return Boolean(schedule)
    && !schedule.deleted
    && !['CANCELLED', 'INACTIVE', 'DELETED'].includes(status);
};

const isInterviewProduct = (course) => {
  const subtype = course?.productCategorySubType
    || course?.product?.productCategorySubType
    || course?.scheduledProduct?.productCategorySubType;
  if (typeof subtype === 'string') return subtype.toUpperCase() === 'INTERVIEW';
  return ['name', 'code', 'typeCode'].some((key) => String(subtype?.[key] || '').toUpperCase() === 'INTERVIEW');
};

const getTodayInputValue = () => new Date().toISOString().slice(0, 10);
const toLocalDateTime = (date, time = '00:00:00') => `${date}T${String(time).slice(0, 8)}`;
const slotKey = (slot) => [slot?.assignedProductId, slot?.date, slot?.startTime, slot?.endTime].join('-');

/**
 * Real course detail for the Exam detail page, resolved from the route's `?primaryId=` value:
 *   • the catalog stores the selected product before navigating, but the URL remains the source
 *     of truth so refreshes cannot render a stale or unrelated course;
 *   • curriculum from `getCurriculumsByPrimaryId(primaryId, ['EXAM'])`;
 *   • schedules from `getScheduledProductsByProductCode(primaryId)`, only when a schedule is
 *     required, the domain is not RETAIL, and the course is not UPCOMING.
 */
const useExamCourseDetail = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const companyId = useSelector((state) => (
    state.companyReducer?.company?.primaryId ?? state.company?.companyDetails?.primaryId
  ));
  const companyState = useSelector((state) => state.companyReducer?.company || state.company?.companyDetails);

  const primaryIdParam = React.useMemo(() => (
    new URLSearchParams(location.search).get('primaryId')
  ), [location.search]);
  const [course, setCourse] = React.useState(null);
  const [curriculums, setCurriculums] = React.useState([]);
  const [curriculumLoading, setCurriculumLoading] = React.useState(false);
  const [curriculumError, setCurriculumError] = React.useState(null);
  const [schedules, setSchedules] = React.useState([]);
  const [loading, setLoading] = React.useState(Boolean(primaryIdParam));
  const [interviewDate, setInterviewDate] = React.useState(getTodayInputValue());
  const [interviewSlots, setInterviewSlots] = React.useState([]);
  const [selectedInterviewSlot, setSelectedInterviewSlot] = React.useState(null);
  const [interviewSlotsLoading, setInterviewSlotsLoading] = React.useState(false);

  // Resolve the course record.
  React.useEffect(() => {
    const requestedPrimaryId = primaryIdParam;
    if (!requestedPrimaryId) {
      setCourse(null);
      setLoading(false);
      return undefined;
    }

    // The URL identifies the requested course. Do not let a stale Redux selection render
    // while a refreshed or directly opened course is being resolved.
    setCourse(null);

    if (!companyId) {
      setLoading(true);
      return undefined;
    }

    let mounted = true;
    setLoading(true);
    dispatch(getCourseProductById(companyId, requestedPrimaryId, false))
      .then((response) => {
        if (!mounted) return;
        const match = asList(response).find((item) => String(item?.primaryId) === String(requestedPrimaryId));
        setCourse(match || null);
      })
      .catch(() => mounted && setCourse(null))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [companyId, dispatch, primaryIdParam]);

  // Curriculum + schedules for the resolved course.
  React.useEffect(() => {
    const primaryId = course?.primaryId;
    if (!primaryId) {
      setCurriculums([]);
      setCurriculumLoading(false);
      setCurriculumError(null);
      setSchedules([]);
      return undefined;
    }

    let mounted = true;
    setCurriculumLoading(true);
    setCurriculumError(null);
    dispatch(getCurriculumsByPrimaryId(primaryId, ['QUESTION', 'PRODUCT', 'COMPANY', 'CHAPTER']))
      .then((data) => {
        if (mounted) setCurriculums(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        if (mounted) {
          setCurriculums([]);
          setCurriculumError(error);
        }
      })
      .finally(() => mounted && setCurriculumLoading(false));

    const domain = localStorage.getItem('domainType');
    if (domain !== 'RETAIL' && scheduleRequired(course) && course.status !== 'UPCOMING') {
      dispatch(getScheduledProductsByProductCode(primaryId))
        .then((data) => mounted && setSchedules((Array.isArray(data) ? data : []).filter(isValidExamSchedule)))
        .catch(() => mounted && setSchedules([]));
    } else {
      setSchedules([]);
    }

    return () => {
      mounted = false;
    };
  }, [course, dispatch]);

  React.useEffect(() => {
    if (!isInterviewProduct(course) || !companyId || !interviewDate) {
      setInterviewSlots([]);
      setSelectedInterviewSlot(null);
      return undefined;
    }

    let mounted = true;
    setInterviewSlotsLoading(true);
    setSelectedInterviewSlot(null);
    const timeZone = companyState?.operatingLocation?.zoneId
      || Intl.DateTimeFormat().resolvedOptions().timeZone
      || 'UTC';
    const workingSchedule = {
      ...companyState?.workingSchedule,
      workingDays: companyState?.workingSchedule?.workingDays || [],
      holidays: companyState?.workingSchedule?.holidays || [],
      openHours: companyState?.workingSchedule?.openHours || '08:00',
      closeHours: companyState?.workingSchedule?.closeHours || '18:00',
      slotIntervalInMinutes: companyState?.workingSchedule?.slotIntervalInMinutes || 60,
    };

    dispatch(getReservationSlots({
      company: { primaryId: companyId, domain: { type: 'EDUCATION' }, workingSchedule },
      criteria: {
        durationType: 'HOUR',
        product: { primaryId: course.primaryId, productCategorySubType: 'INTERVIEW' },
        searchPeriod: { effectiveDate: { localStartDate: toLocalDateTime(interviewDate), timeZone } },
      },
    }))
      .then((response) => {
        if (!mounted) return;
        const groups = response?.matchingProductGroups || response?.data?.matchingProductGroups || [];
        const slots = groups.flatMap((group) => group?.slots || [])
          .filter((slot) => slot?.date && slot?.startTime && slot?.endTime);
        setInterviewSlots(slots.filter((slot, index, list) => (
          list.findIndex((item) => slotKey(item) === slotKey(slot)) === index
        )));
      })
      .catch(() => mounted && setInterviewSlots([]))
      .finally(() => mounted && setInterviewSlotsLoading(false));

    return () => { mounted = false; };
  }, [companyId, companyState, course, dispatch, interviewDate]);

  const routeCourse = course
    && primaryIdParam
    && String(course.primaryId) === String(primaryIdParam)
    ? course
    : null;

  return {
    course: routeCourse,
    hasCourseSelection: Boolean(primaryIdParam),
    company: companyState,
    curriculums,
    curriculumLoading,
    curriculumError,
    schedules,
    loading,
    isScheduleRequired: scheduleRequired(routeCourse),
    interviewDate,
    setInterviewDate,
    interviewSlots,
    interviewSlotsLoading,
    selectedInterviewSlot,
    setSelectedInterviewSlot,
  };
};

/**
 * Course detail plus the live enrolment action, so "Register for this exam" runs the same flow
 * as the Default course page (shared implementation in views/products/useCoursePurchase.js).
 */
const useExamCourseDetailWithPurchase = () => {
  const detail = useExamCourseDetail();
  const { purchase, isProcessing, alert, dismissAlert } = useCoursePurchase();
  const [selectedSchedule, setSelectedSchedule] = React.useState(null);

  React.useEffect(() => {
    if (detail.schedules?.length === 1) {
      setSelectedSchedule(detail.schedules[0]);
    } else {
      setSelectedSchedule(null);
    }
  }, [detail.course?.primaryId, detail.schedules]);

  const register = React.useCallback(() => {
    purchase(detail.course, {
      isScheduleRequired: detail.isScheduleRequired,
      selectedSchedule,
      interviewSlot: detail.selectedInterviewSlot
    });
  }, [detail.course, detail.isScheduleRequired, detail.selectedInterviewSlot, purchase, selectedSchedule]);

  return {
    ...detail,
    register,
    isProcessing,
    alert,
    dismissAlert,
    selectedSchedule,
    onSelectSchedule: setSelectedSchedule,
    isInterviewProduct: isInterviewProduct(detail.course),
    interviewDate: detail.interviewDate,
    onInterviewDateChange: detail.setInterviewDate,
    interviewSlots: detail.interviewSlots,
    interviewSlotsLoading: detail.interviewSlotsLoading,
    selectedInterviewSlot: detail.selectedInterviewSlot,
    onSelectInterviewSlot: detail.setSelectedInterviewSlot
  };
};

/**
 * Opens a course exactly as the Default template's course page does:
 * `dispatch(setSelectedProduct(product))` then navigate to `/course-detail?primaryId=...`
 * (see handleEnrollNow in views/products/index.jsx). CourseInfo reads the selected product from
 * that store, so upcoming courses render through the same detail page state as the default page.
 */
const useSelectCourse = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return React.useCallback((course) => {
    if (!course?.primaryId) return;
    dispatch(setSelectedProduct(course));
    navigate(getExamCourseDetailPath(course));
  }, [dispatch, navigate]);
};

/**
 * Client-side navigation for the Exam sections' internal links. Without this the sections' bare
 * `<a href>` elements trigger a full document reload on every click, losing app state.
 */
const useExamNavigate = () => {
  const navigate = useNavigate();
  return React.useCallback((href) => navigate(href), [navigate]);
};

const getExamAuthPageKey = (section = {}) => {
  const componentKey = section.content?.componentKey || section.bindings?.componentKey;
  const previewKey = section.content?.previewKey || section.bindings?.previewKey;
  if (previewKey === 'authSignIn' || componentKey === 'Login') return 'signIn';
  if (previewKey === 'authSignUp' || componentKey === 'Signup') return 'signUp';
  return '';
};

const ExamAuthPanel = ({ pageKey }) => {
  const AuthComponent = pageKey === 'signUp' ? Signup : Login;
  return (
    <div className={`aae-exam-auth aae-exam-auth-${pageKey}`}>
      <AuthComponent templateDomainType={EDUCATION} />
    </div>
  );
};

export const EducationExamPagesSectionWithAuth = (props) => (
  <EducationExamPagesSection
    {...props}
    data={useExamSectionData(props.data)}
    auth={useExamNavAuth()}
    onSelectCourse={useSelectCourse()}
    onNavigate={useExamNavigate()}
    courseDetail={useExamCourseDetailWithPurchase()}
  />
);

/**
 * Functional Exam pages (cart, order history, sign-in, sign-up): the Exam frame wraps the
 * real live component, which is mounted through the standard ExistingComponentSection path so
 * checkout and auth behaviour is untouched.
 */
export const EducationExamShellSectionWithAuth = ({ section, theme, template, ...rest }) => {
  const authPageKey = getExamAuthPageKey(section);

  return (
    <EducationExamPagesSection {...rest} section={section} auth={useExamNavAuth()} onNavigate={useExamNavigate()}>
      {authPageKey ? (
        <ExamAuthPanel pageKey={authPageKey} />
      ) : (
        <ExistingComponentSection section={section} theme={theme} template={template} />
      )}
    </EducationExamPagesSection>
  );
};
